import {
  PatrimoineCategoryEnum,
  SubCategory2Enum,
  SubCategoryEnum,
  TypeActifEnum,
  TypePassif,
  TypePrevoyance,
} from '@lib/models/generated/graphql';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { actifTypeLabel } from './patrimoine.model';

export type CategoriePatrimoine = PatrimoineCategoryEnum | SubCategoryEnum | SubCategory2Enum;

export interface PatrimoineFormModel {
  types: TypePatrimoine[];
}

export interface TypePatrimoine {
  value: string;
  label: string;
  categorie: CategoriePatrimoine;
}

const makeActif = <T extends SubCategory2Enum | SubCategoryEnum>(value: TypeActifEnum, categorie: T): TypePatrimoine => ({
  label: actifTypeLabel[value],
  value,
  categorie,
});

const group = <T extends SubCategory2Enum | SubCategoryEnum>(categorie: T, values: TypeActifEnum[]): TypePatrimoine[] =>
  values.map(v => makeActif(v, categorie));

export const immobilierTypes: TypePatrimoine[] = [
  ...group(SubCategory2Enum.ImmoJouissance, [TypeActifEnum.ResidencePrincipale, TypeActifEnum.ResidenceSecondaire]),

  ...group(SubCategory2Enum.ImmoLocatif, [
    TypeActifEnum.Classique,
    TypeActifEnum.Pinel,
    TypeActifEnum.Duflot,
    TypeActifEnum.Scellier,
    TypeActifEnum.Robien,
    TypeActifEnum.Borloo,
    TypeActifEnum.Besson,
    TypeActifEnum.Perissol,
    TypeActifEnum.LmnpClassique,
    TypeActifEnum.LmnpCensi,
    TypeActifEnum.Lmp,
    TypeActifEnum.DemembrementTemporaire,
    TypeActifEnum.Malraux,
    TypeActifEnum.MonumentHistorique,
    TypeActifEnum.Girardin,
    TypeActifEnum.LoiJego,
    TypeActifEnum.SecteurTouristique,
    TypeActifEnum.Opci,
  ]),

  ...group(SubCategory2Enum.Foncier, [
    TypeActifEnum.TerrainABatir,
    TypeActifEnum.TerreAgricole,
    TypeActifEnum.BienRuralBailALongTerme,
    TypeActifEnum.BoisEtPartsDeGff,
    TypeActifEnum.PartsDeGfa,
    TypeActifEnum.PartsGfv,
    TypeActifEnum.TerrainDAgrement,
    TypeActifEnum.ScIouSarlDeFamille,
    TypeActifEnum.AutreFoncierNonBati,
  ]),
];

export const financierTypes: TypePatrimoine[] = [
  ...group(SubCategory2Enum.EpargneBancaire, [
    TypeActifEnum.LivretA,
    TypeActifEnum.CompteCourant,
    TypeActifEnum.LivretClassique,
    TypeActifEnum.LivretBleu,
    TypeActifEnum.Ldd,
    TypeActifEnum.Lep,
    TypeActifEnum.Cel,
    TypeActifEnum.LivretJeune,
    TypeActifEnum.Cat,
    TypeActifEnum.AutreLiquidite,
    TypeActifEnum.Pel,
  ]),

  ...group(SubCategory2Enum.EpargneFinanciere, [
    TypeActifEnum.AssuranceVie,
    TypeActifEnum.ContratDeCapitalisation,
    TypeActifEnum.PepBancaire,
    TypeActifEnum.PepAssuranceVie,
    TypeActifEnum.CompteTitresCto,
    TypeActifEnum.AssuranceVieApbDifferee,
    TypeActifEnum.AutreValeurMobilier,
    TypeActifEnum.AutreEpargne,
    TypeActifEnum.Pea,
    TypeActifEnum.Peapme,
  ]),

  ...group(SubCategory2Enum.EpargneImmobiliere, [TypeActifEnum.Scpi]),

  ...group(SubCategory2Enum.Diversification, [
    TypeActifEnum.CapitalRisque,
    TypeActifEnum.Fip,
    TypeActifEnum.FipCorse,
    TypeActifEnum.Fcpi,
    TypeActifEnum.Pme,
    TypeActifEnum.Sofica,
    TypeActifEnum.Fcpr,
    TypeActifEnum.Holding,
    TypeActifEnum.AutresTitresNonCotes,
    TypeActifEnum.FipOutreMer,
  ]),

  ...group(SubCategory2Enum.EpargneRetraite, [
    TypeActifEnum.Perp,
    TypeActifEnum.Pee,
    TypeActifEnum.Perco,
    TypeActifEnum.Article82,
    TypeActifEnum.Article83,
    TypeActifEnum.Article39,
    TypeActifEnum.Madelin,
    TypeActifEnum.Participation,
    TypeActifEnum.RenteUniverselle,
    TypeActifEnum.EpargnePension,
    TypeActifEnum.Eip,
    TypeActifEnum.Plci,
    TypeActifEnum.Cpti,
    TypeActifEnum.Elt,
    TypeActifEnum.Per,
    TypeActifEnum.Prefon,
    TypeActifEnum.Inami,
  ]),
];

export const biensProfessionelsTypes: TypePatrimoine[] = group(SubCategoryEnum.BiensProfessionnels, [
  TypeActifEnum.PartsDeSociete,
  TypeActifEnum.BienImmobilier,
  TypeActifEnum.FondsDeCommerce,
  TypeActifEnum.ClienteleCivile,
  TypeActifEnum.MaterielProfessionnel,
  TypeActifEnum.VehiculeProfessionnel,
]);

export const autresBiensMeublesTypes: TypePatrimoine[] = group(SubCategoryEnum.AutresBiensMeubles, [
  TypeActifEnum.Meuble,
  TypeActifEnum.Bijoux,
  TypeActifEnum.Vehicules,
  TypeActifEnum.ObjetDArtAntiquiteCollection,
  TypeActifEnum.MetauxPrecieux,
  TypeActifEnum.AutrePlacement,
  TypeActifEnum.Creance,
]);

export const autres: TypePatrimoine[] = group(SubCategory2Enum.AutresFinancier, [TypeActifEnum.ContratTontinier]);

export const actifTypes: TypePatrimoine[] = [
  ...immobilierTypes,
  ...financierTypes,
  ...biensProfessionelsTypes,
  ...autresBiensMeublesTypes,
  ...autres,
];

// export const creditTypes: TypePatrimoine[] = generatePassifs([SubCategory2Enum.CreditImmo]);
// export const autrePassifTypes: TypePatrimoine[] = generatePassifs([
//   SubCategory2Enum.CreditConso,
//   SubCategory2Enum.AutreCredit,
//   SubCategory2Enum.Dettes,
//   SubCategory2Enum.Avance,
// ]);

// export const passifTypes: TypePatrimoine[] = [...creditTypes, ...autrePassifTypes];

export const passifTypes: TypePatrimoine[] = [
  { label: 'Crédit immobilier', value: SubCategory2Enum.CreditImmo, categorie: SubCategoryEnum.Credits },
  { label: 'Crédit conso ou crédit revolving', value: SubCategory2Enum.CreditConso, categorie: SubCategoryEnum.AutresPassif },
  { label: 'Autre crédit', value: SubCategory2Enum.AutreCredit, categorie: SubCategoryEnum.AutresPassif },
  { label: 'Dette fiscale', value: SubCategory2Enum.Dettes, categorie: SubCategoryEnum.AutresPassif },
  { label: 'Avance', value: SubCategory2Enum.Avance, categorie: SubCategoryEnum.AutresPassif },
];

export const passifNatureOptions: OuiSelectOption<TypePassif>[] = [
  { label: 'Amortissable', value: TypePassif.Amortissable },
  { label: 'In fine', value: TypePassif.InFine },
  { label: 'Par palier', value: TypePassif.ParPalier },
  { label: 'Simplifié', value: TypePassif.Simplifie },
];

export const prevoyanceTypes: TypePatrimoine[] = [
  { label: 'Temporaire décès', value: TypePrevoyance.TemporaireDeces, categorie: PatrimoineCategoryEnum.Prevoyance },
  { label: 'Vie entière', value: TypePrevoyance.VieEntiere, categorie: PatrimoineCategoryEnum.Prevoyance },
  { label: 'Capital différé', value: TypePrevoyance.CapitalDiffere, categorie: PatrimoineCategoryEnum.Prevoyance },
  {
    label: 'Contrat de prévoyance individuel',
    value: TypePrevoyance.ContratDePrevoyanceIndividuel,
    categorie: PatrimoineCategoryEnum.Prevoyance,
  },
  { label: 'Autre', value: TypePrevoyance.Autre, categorie: PatrimoineCategoryEnum.Prevoyance },
];

// function generatePassifs(categories: SubCategory2Enum[]): TypePatrimoine[] {
//   const result: TypePatrimoine[] = [];
//   for (const cat of categories) {
//     result.push(
//       ...[
//         {
//           label: 'Amortissable',
//           value: TypePassif.Amortissable,
//           categorie: cat,
//         },
//         {
//           label: 'In fine',
//           value: TypePassif.InFine,
//           categorie: cat,
//         },
//         {
//           label: 'Simplifié',
//           value: TypePassif.Simplifie,
//           categorie: cat,
//         },
//         {
//           label: 'Par palier',
//           value: TypePassif.ParPalier,
//           categorie: cat,
//         },
//       ]
//     );
//   }

//   return result;
// }
